import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
  Plus,
  Users,
  Target,
  TrendingUp,
  Clock3,
  BarChart3,
  Search,
  CalendarClock,
} from "lucide-react";
import api from "@/services/api";
import PageHeader from "@/components/PageHeader";
import StateView from "@/components/StateView";

interface AppelOffre {
  id: string;
  titre: string;
  reference: string;
  date_limite?: string | null;
  created_at: string;
  statut: 'en cours' | 'publié' | 'terminé';
}
interface DashboardStats {
  totalAO: number;
  aoEnCours: number;
  aoPublie: number;
  aoTermine: number;
  totalCV: number;
  analyses: number;
  totalTokens: number;
  totalCost: number;
}
const Dashboard = () => {
  const [appelOffres, setAppelOffres] = useState<AppelOffre[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalAO: 0,
    aoEnCours: 0,
    aoPublie: 0,
    aoTermine: 0,
    totalCV: 0,
    analyses: 0,
    totalTokens: 0,
    totalCost: 0
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const navigate = useNavigate();
  const { userRole } = useAuth();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setHasError(false);

      const { data: response } = await api.get('/dashboard/stats');
      const dashboardData = response?.data || {};

      setAppelOffres(dashboardData.recentAO || []);
      setStats({
        totalAO: dashboardData.totalAO || 0,
        aoEnCours: dashboardData.aoEnCours || 0,
        aoPublie: dashboardData.aoPublie || 0,
        aoTermine: dashboardData.aoTermine || 0,
        totalCV: dashboardData.totalCV || 0,
        analyses: dashboardData.totalAnalyses || 0,
        totalTokens: dashboardData.totalTokens || 0,
        totalCost: dashboardData.totalCost || 0
      });
    } catch (error) {
      console.error('Erreur lors du chargement du dashboard:', error);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  const filteredAO = useMemo(
    () =>
      appelOffres.filter(
        (ao) =>
          ao.titre.toLowerCase().includes(searchTerm.toLowerCase()) ||
          ao.reference.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [appelOffres, searchTerm]
  );

  const getStatusBadge = (statut: 'en cours' | 'publié' | 'terminé') => {
    switch (statut) {
      case 'en cours':
        return <Badge className="bg-warning text-warning-foreground">En cours</Badge>;
      case 'publié':
        return <Badge className="bg-success text-success-foreground">Publié</Badge>;
      case 'terminé':
        return <Badge className="bg-destructive text-destructive-foreground">Terminé</Badge>;
      default:
        return <Badge variant="secondary">Inconnu</Badge>;
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) {
      return "Non définie";
    }
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <StateView
        state="loading"
        title="Chargement du dashboard"
        description="Récupération des indicateurs en cours."
      />
    );
  }

  if (hasError) {
    return (
      <StateView
        state="error"
        title="Impossible de charger le dashboard"
        description="Les données n'ont pas pu être récupérées."
        action={
          <Button variant="outline" size="sm" onClick={loadDashboardData}>
            Réessayer
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description="Vue d'ensemble des analyses AO et des indicateurs opérationnels."
        actions={
          userRole === 'admin' ? (
            <Button onClick={() => navigate('/appels-offres/nouveau')}>
              <Plus className="mr-2 h-4 w-4" />
              Créer une analyse
            </Button>
          ) : null
        }
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
        <Card className="card-shadow bg-primary-light">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
            <CardTitle className="text-xs font-medium">Analyses créées</CardTitle>
            <Target className="h-3 w-3 text-primary" />
          </CardHeader>
          <CardContent className="space-y-1 py-2">
            <div className="text-xl font-bold text-primary">{stats.totalAO}</div>
            <p className="text-xs text-muted-foreground">Tous appels d'offres confondus</p>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
            <CardTitle className="text-xs font-medium">Répartition par statut</CardTitle>
            <Clock3 className="h-3 w-3 text-muted-foreground" />
          </CardHeader>
          <CardContent className="space-y-2 py-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">En cours</span>
              <span className="text-sm font-semibold text-warning">{stats.aoEnCours}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Publié</span>
              <span className="text-sm font-semibold text-success">{stats.aoPublie}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Terminé</span>
              <span className="text-sm font-semibold text-destructive">{stats.aoTermine}</span>
            </div>
          </CardContent>
        </Card>

        {userRole === 'admin' && (
          <Card className="card-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
              <CardTitle className="text-xs font-medium">Documents importés</CardTitle>
              <Users className="h-3 w-3 text-muted-foreground" />
            </CardHeader>
            <CardContent className="space-y-1 py-2">
              <div className="text-xl font-bold">{stats.totalCV}</div>
              <p className="text-xs text-muted-foreground">Documents reçus</p>
            </CardContent>
          </Card>
        )}

        {userRole === 'admin' && (
          <Card className="card-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
              <CardTitle className="text-xs font-medium">Analyses IA</CardTitle>
              <TrendingUp className="h-3 w-3 text-muted-foreground" />
            </CardHeader>
            <CardContent className="space-y-1 py-2">
              <div className="text-xl font-bold text-accent">{stats.analyses}</div>
              <p className="text-xs text-muted-foreground">Scores générés</p>
            </CardContent>
          </Card>
        )}

        {userRole === 'admin' && (
          <Card className="card-shadow bg-accent-light">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
              <CardTitle className="text-xs font-medium">Tokens & coûts</CardTitle>
              <BarChart3 className="h-3 w-3 text-accent" />
            </CardHeader>
            <CardContent className="space-y-1 py-2">
              <div className="text-xl font-bold text-accent">{stats.totalTokens.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">tokens • CHF {stats.totalCost.toFixed(2)}</p>
            </CardContent>
          </Card>
        )}

        <Card
          className="card-shadow cursor-pointer transition-smooth hover:bg-muted/30"
          onClick={() => navigate('/planning-ao?view=gantt')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
            <CardTitle className="text-xs font-medium">Planning Gantt</CardTitle>
            <CalendarClock className="h-3 w-3 text-primary" />
          </CardHeader>
          <CardContent className="space-y-1 py-2">
            <div className="text-xl font-bold text-primary">Planning</div>
            <p className="text-xs text-muted-foreground">Vue chronologique des AO</p>
          </CardContent>
        </Card>
      </div>

      <Card className="card-shadow">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-lg">Appels d'offres récents</CardTitle>
            <CardDescription>Accès rapide aux derniers dossiers créés.</CardDescription>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Rechercher un AO..."
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          {filteredAO.length === 0 ? (
            <StateView
              state="empty"
              className="min-h-[180px]"
              title="Aucun AO trouvé"
              description="Ajustez la recherche ou créez une nouvelle analyse."
            />
          ) : (
            <div className="divide-y divide-border rounded-md border border-border/70">
              {filteredAO.slice(0, 8).map((ao) => (
                <button
                  key={ao.id}
                  type="button"
                  onClick={() => navigate(`/appels-offres/${ao.id}`)}
                  className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-muted/40"
                >
                  <div className="min-w-0 space-y-1">
                    <p className="truncate text-sm font-medium text-foreground">{ao.titre}</p>
                    <p className="text-xs text-muted-foreground">{ao.reference}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <p className="text-xs text-muted-foreground">{formatDate(ao.date_limite)}</p>
                    {getStatusBadge(ao.statut)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
export default Dashboard;
